import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { SafeUrl } from "@angular/platform-browser";
import {
  ApiId,
  AppRecord,
  AppRecord$,
  ATTACHMENT_MODEL,
  Dictionary,
  EnqueuedGroup,
  EnqueuedRequest,
  EnqueueOptions,
  IAppRecord,
  IOfflineRecord,
  IMany2ManyValues,
  IOne2ManyItem,
  Many2Many,
  Many2One,
  OFFLINE_QUEUE_MODEL,
  One2Many,
} from "@app/models";
import { AuthService, ErrorService } from "@app/services";
import { StorageService } from "@app/services/storage.service";
import { Capacitor } from "@capacitor/core";
import { Platform } from "@ionic/angular";
import { isEqual } from "lodash";
import {
  defer,
  EMPTY,
  forkJoin,
  from,
  Observable,
  of,
  Subject,
  throwError,
} from "rxjs";
import {
  catchError,
  concatMap,
  expand,
  filter,
  finalize,
  last,
  map,
  mergeMap,
  switchMap,
  take,
  tap,
  timeout,
  toArray,
} from "rxjs/operators";
import * as uuid from "uuid";
import { validate as uuidValidate } from "uuid";
import { environment } from "../../environments/environment";
import { NetworkService } from "../services/network.service";
import { OdooService } from "./odoo.service";
import { PouchService } from "./pouch.service";

export const ENSURE_FILE_TIMEOUT = 120000;
export const UPDATE_RECONCILIATION_TIMEOUT = 10000;
export const CREATE_RECONCILIATION_TIMEOUT = 10000;

class EnqueuedGroupFailureHandled extends Error {
  constructor(public readonly originalError: unknown) {
    super("Offline queue group failure was persisted");
  }
}

class UpdateProjectionSuperseded extends Error {}

interface RemoteUpdateCommit {
  model: string;
  apiId: ApiId;
  odooId: ApiId;
  localValues: IAppRecord;
  submittedValues: IAppRecord;
  expectedServerVersion?: string;
  serverVersion?: string;
  recordGeneration: number;
  scopeGeneration: number;
}

interface RemoteCreateCommit {
  model: string;
  apiId: ApiId;
  record: IAppRecord;
  recordGeneration: number;
  scopeGeneration: number;
}

interface ScopedSearchProjection {
  model: string;
  scopeKey: string;
  records: IAppRecord[];
  cacheGeneration: number;
  searchGeneration: number;
  scopeGeneration: number;
}

interface QueuedRequestIds {
  dirty: Set<string>;
  deleted: Set<string>;
}

type CommittedEnqueuedRequest = EnqueuedRequest & {
  remoteCreateCommit?: RemoteCreateCommit;
  remoteUpdateCommit?: RemoteUpdateCommit;
  remoteDeleteCommitted?: boolean;
};

@Injectable({
  providedIn: "root",
})
export class ApiService {
  private http = inject(HttpClient);
  private platform = inject(Platform);
  private authService = inject(AuthService);
  private odooService = inject(OdooService);
  private pouchService = inject(PouchService);
  private errorService = inject(ErrorService);
  private networkService = inject(NetworkService);
  private storageService = inject(StorageService);
  private cacheGenerations = new Map<string, number>();
  private recordGenerations = new Map<string, number>();
  private cacheMutationTails = new Map<string, Promise<void>>();
  private scopedSearchGenerations = new Map<string, number>();
  private unconfirmedCreateIds = new Map<string, Set<string>>();
  private createReconciliation = new Subject<RemoteCreateCommit>();
  private scopedSearchProjection = new Subject<ScopedSearchProjection>();

  starter = new Subject<EnqueuedGroup>();

  syncEnqueue = new Subject<EnqueuedGroup>();
  syncStarted = new Subject<EnqueuedGroup>();
  syncSuccess = new Subject<EnqueuedGroup>();
  syncFailure = new Subject<EnqueuedGroup>();

  createSuccess = new Subject<{ model: string; data: any }>();
  updateSuccess = new Subject<{ model: string; data: any }>();
  deleteSuccess = new Subject<{ model: string; id: ApiId }>();

  constructor() {
    this.pouchService.onScopeChange().subscribe(() => {
      this.cacheGenerations.clear();
      this.recordGenerations.clear();
      this.cacheMutationTails.clear();
      this.scopedSearchGenerations.clear();
      this.unconfirmedCreateIds.clear();
    });
    this.createReconciliation
      .pipe(
        mergeMap((commit) =>
          this.reconcileCommittedCreate(commit).pipe(
            catchError((error) => {
              console.error("Odoo create reconciliation failed", error);
              return EMPTY;
            })
          )
        )
      )
      .subscribe();
    this.scopedSearchProjection
      .pipe(
        mergeMap((projection) =>
          this.projectScopedSearch(projection).pipe(
            catchError((error) => {
              console.error("Scoped search cache projection failed", error);
              return EMPTY;
            })
          )
        )
      )
      .subscribe();
  }

  private getCacheGeneration(model: string): number {
    return this.cacheGenerations.get(model) ?? 0;
  }

  private bumpCacheGeneration(model: string): void {
    this.cacheGenerations.set(model, this.getCacheGeneration(model) + 1);
  }

  private nextScopedSearchGeneration(scopeKey: string): number {
    const generation = (this.scopedSearchGenerations.get(scopeKey) ?? 0) + 1;
    this.scopedSearchGenerations.set(scopeKey, generation);
    return generation;
  }

  private markCreateUnconfirmed(model: string, record: IAppRecord): void {
    const ids = this.unconfirmedCreateIds.get(model) ?? new Set<string>();
    [record._id, record.id]
      .filter((id) => id !== null && id !== undefined)
      .forEach((id) => ids.add(String(id)));
    this.unconfirmedCreateIds.set(model, ids);
  }

  private clearConfirmedCreate(
    model: string,
    ...records: IAppRecord[]
  ): void {
    const ids = this.unconfirmedCreateIds.get(model);
    if (!ids) return;
    records.forEach((record) =>
      [record?._id, record?.id]
        .filter((id) => id !== null && id !== undefined)
        .forEach((id) => ids.delete(String(id)))
    );
    if (ids.size === 0) this.unconfirmedCreateIds.delete(model);
  }

  private clearUnconfirmedCreateIds(model: string, ...recordIds: ApiId[]): void {
    const ids = this.unconfirmedCreateIds.get(model);
    if (!ids) return;
    recordIds
      .filter((id) => id !== null && id !== undefined)
      .forEach((id) => ids.delete(String(id)));
    if (ids.size === 0) this.unconfirmedCreateIds.delete(model);
  }

  private getQueuedRequestIds(
    queueGroups: IOfflineRecord[],
    model: string
  ): QueuedRequestIds {
    const result: QueuedRequestIds = {
      dirty: new Set<string>(),
      deleted: new Set<string>(),
    };
    queueGroups.forEach((group) =>
      ((group["queue"] ?? []) as EnqueuedRequest[])
        .filter((request) => request.model === model)
        .forEach((request) => {
          const target = request.method === "delete" ? result.deleted : result.dirty;
          if (request.id !== null && request.id !== undefined) {
            target.add(String(request.id));
          }
        })
    );
    return result;
  }

  private getProtectedRequestIds(
    queueGroups: IOfflineRecord[],
    model: string
  ): Set<string> {
    const { dirty, deleted } = this.getQueuedRequestIds(queueGroups, model);
    return new Set([...dirty, ...deleted]);
  }

  private recordMatchesIds(
    record: Partial<IAppRecord>,
    ids: Set<string>,
    rawPouchId = false
  ): boolean {
    const recordIds = [
      rawPouchId && record._id
        ? this.pouchService.unslugId(record._id)
        : record._id,
      record.id,
    ];
    return recordIds.some(
      (id) =>
        (typeof id === "string" || typeof id === "number") &&
        ids.has(String(id))
    );
  }

  private recordsShareIdentity(
    first: Partial<IAppRecord>,
    second: Partial<IAppRecord>
  ): boolean {
    const firstIds = [first.id, first._id]
      .filter(
        (id): id is ApiId =>
          typeof id === "string" || typeof id === "number"
      )
      .map(String);
    return [second.id, second._id].some(
      (id) =>
        (typeof id === "string" || typeof id === "number") &&
        firstIds.includes(String(id))
    );
  }

  private getRecordGeneration(model: string, apiId: ApiId): number {
    return this.recordGenerations.get(`${model}:${apiId}`) ?? 0;
  }

  private getServerVersion(values: unknown): string | undefined {
    const record = values as
      | { server_version?: string; _server_version?: string }
      | null
      | undefined;
    return record?.server_version ?? record?._server_version;
  }

  private bumpMutationGeneration(model: string, apiId: ApiId): number {
    this.bumpCacheGeneration(model);
    const key = `${model}:${apiId}`;
    const generation = this.getRecordGeneration(model, apiId) + 1;
    this.recordGenerations.set(key, generation);
    return generation;
  }

  /**
   * PouchDB refreshes and CRUD projections for one model must be serialized.
   * A generation check alone cannot protect the gap between reading the
   * generation and deleting/replacing cached documents.
   */
  private runCacheMutation<T>(
    model: string,
    operation: () => Observable<T>
  ): Observable<T> {
    return defer(() => {
      const previous = this.cacheMutationTails.get(model) ?? Promise.resolve();
      let release!: () => void;
      const gate = new Promise<void>((resolve) => {
        release = resolve;
      });
      const tail = previous.catch(() => undefined).then(() => gate);
      this.cacheMutationTails.set(model, tail);

      return from(previous.catch(() => undefined)).pipe(
        switchMap(() => operation()),
        finalize(() => {
          release();
          if (this.cacheMutationTails.get(model) === tail) {
            this.cacheMutationTails.delete(model);
          }
        })
      );
    });
  }

  private runRemoteMutation<T>(
    model: string,
    operation: () => Observable<T>
  ): Observable<T> {
    return defer(() => {
      // Change the epoch at both boundaries. A search started before or while
      // the remote mutation is in flight can therefore never commit its
      // snapshot after the mutation settles (successfully or with an error).
      this.bumpCacheGeneration(model);
      return operation().pipe(
        finalize(() => this.bumpCacheGeneration(model))
      );
    });
  }

  public downloadFile(
    model: string,
    fileId: ApiId,
    fileName: string,
    mimeType: string
  ): Observable<string | void> {
    const scopeGeneration = this.pouchService.getScopeGeneration();
    if (this.platform.is("hybrid")) {
      return this.pouchService.bindToScope(this.storageService
        .shareFile(model, fileId, fileName)
        .pipe(catchError((error) => this.errorService.handleError(error))), scopeGeneration);
    }

    return this.pouchService.bindToScope(this.storageService.readFile(model, fileId).pipe(
      tap((fileData: string) => {
        this.storageService.downloadBase64(fileData, fileName, mimeType);
      })
    ), scopeGeneration);
  }

  public downloadReport(report: string, fileId: ApiId, fileName: string) {
    const scopeGeneration = this.pouchService.getScopeGeneration();
    const report$ = this.isOnline()
      ? this.fetchReport(report, fileId).pipe(
          switchMap((reportData) =>
            this.storageService
              .writeFile(report, fileId, reportData)
              .pipe(map(() => reportData))
          )
        )
      : this.storageService.readFile(report, fileId);

    if (this.platform.is("hybrid")) {
      return throwError(
        () => new Error("Download for hybrid app is not implemented")
      );
    } else {
      return this.pouchService.bindToScope(report$.pipe(
        tap((reportData) => {
          this.storageService.downloadBase64(
            reportData,
            fileName,
            "application/pdf"
          );
        }),
        catchError((error) => this.errorService.handleError(error))
      ), scopeGeneration);
    }
  }

  public ensureFile(
    model: string,
    apiId: ApiId,
    mimeType: string
  ): Observable<SafeUrl> {
    const scopeGeneration = this.pouchService.getScopeGeneration();
    return this.pouchService.bindToScope(this.storageService.getStats(model, apiId).pipe(
      catchError(() =>
        this.fetchFile(apiId).pipe(
          switchMap(({ datas }) =>
            this.storageService.writeFile(model, apiId, datas)
          )
        )
      ),
      timeout(ENSURE_FILE_TIMEOUT),
      switchMap(() =>
        this.storageService.readFile(model, apiId).pipe(
          switchMap((fileData) => {
            const blob = this.storageService.base64ToBlob(fileData, mimeType);
            return this.storageService.getThumbnail(blob);
          })
        )
      ),
      catchError((error) => this.errorService.handleError(error))
    ), scopeGeneration);
  }

  public ensureReport(report: string, apiId: ApiId) {
    const scopeGeneration = this.pouchService.getScopeGeneration();
    return this.pouchService.bindToScope(this.storageService.getStats(report, apiId).pipe(
      catchError(() =>
        this.fetchReport(report, apiId).pipe(
          switchMap((reportData) =>
            this.storageService.writeFile(report, apiId, reportData)
          )
        )
      ),
      timeout(ENSURE_FILE_TIMEOUT),
      catchError((error) => this.errorService.handleError(error))
    ), scopeGeneration);
  }

  public saveFile(
    model: string,
    fileId: ApiId,
    blob: Blob
  ): Observable<SafeUrl> {
    const scopeGeneration = this.pouchService.getScopeGeneration();
    return this.pouchService.bindToScope(this.storageService.blobToBase64(blob).pipe(
      switchMap((base64) =>
        this.storageService.writeFile(model, fileId, base64)
      ),
      switchMap(() => this.storageService.getThumbnail(blob))
    ), scopeGeneration);
  }

  public openFile(
    model: string,
    apiId: ApiId,
    mimeType: string
  ): Observable<void> {
    const scopeGeneration = this.pouchService.getScopeGeneration();
    if (!this.platform.is("hybrid")) {
      return throwError(() => new Error("Cannot open report in browser"));
    }
    return this.pouchService.bindToScope(this.storageService.getUri(model, apiId).pipe(
      switchMap(({ uri }) => this.storageService.openFile(uri, mimeType)),
      catchError((error) => this.errorService.handleError(error))
    ), scopeGeneration);
  }

  public openReport(report: string, apiId: ApiId): Observable<void> {
    const scopeGeneration = this.pouchService.getScopeGeneration();
    if (this.platform.is("hybrid")) {
      return throwError(() => new Error("Cannot open report in browser"));
    }

    const report$: Observable<string> = this.isOnline()
      ? this.fetchReport(report, apiId).pipe(
          switchMap((reportData) =>
            this.storageService.writeFile(report, apiId, reportData)
          ),
          map(({ uri }) => uri)
        )
      : this.storageService.getUri(report, apiId).pipe(map(({ uri }) => uri));

    return this.pouchService.bindToScope(report$.pipe(
      map((uri) => Capacitor.convertFileSrc(uri)),
      switchMap((path) =>
        this.storageService.openFile(path, "application/pdf")
      ),
      catchError((error) => this.errorService.handleError(error))
    ), scopeGeneration);
  }

  public deleteFile(model: string, fileId: ApiId) {
    const scopeGeneration = this.pouchService.getScopeGeneration();
    return this.pouchService.bindToScope(
      this.storageService.deleteFile(model, fileId),
      scopeGeneration
    );
  }

  public generateAppId() {
    return uuid.v4();
  }

  public isValidAppId(id: ApiId) {
    return typeof id === "string" && uuidValidate(id);
  }

  private preparePouchId(values: any): ApiId {
    return values._id || values.id || this.generateAppId();
  }

  public preparePouchValues(values: any): Observable<any> {
    const _id = this.preparePouchId(values);
    return of({ ...values, _id });
  }

  private prepareOdooId(model: string, id: ApiId): Observable<ApiId> {
    if (!uuidValidate(id.toString())) {
      return of(id).pipe(map((id) => parseInt(id.toString())));
    } else {
      return this.pouchService.read(model, id).pipe(
        map((pouchData: any) => {
          return pouchData.id || id;
        })
      );
    }
  }

  private prepareOdooRelationId(id: string) {
    return this.pouchService.searchId(id);
  }

  public isOnline(): boolean {
    return this.networkService.isOnline();
  }

  public isOffline(): boolean {
    return this.networkService.isOffline();
  }

  public setOffline(
    model: string,
    onlineData: IAppRecord[],
    expectedCacheGeneration = this.getCacheGeneration(model)
  ): Observable<IAppRecord[]> {
    return this.runCacheMutation(model, () =>
      this.reconcileOfflineCache(model, onlineData, expectedCacheGeneration)
    ).pipe(map((records) => this.exposeRecords(records)));
  }

  private reconcileOfflineCache(
    model: string,
    onlineData: IAppRecord[],
    expectedCacheGeneration: number
  ): Observable<IAppRecord[]> {
    return forkJoin({
      offlineData: this.pouchService._search(model),
      queueGroups: this.pouchService._search(OFFLINE_QUEUE_MODEL),
    }).pipe(
      switchMap(({ offlineData, queueGroups }) => {
        const protectedRequestIds = this.getProtectedRequestIds(
          queueGroups,
          model
        );
        onlineData.forEach((onlineRecord) => {
          const matchingOffline = offlineData.find((offlineRecord) =>
            this.recordsShareIdentity(offlineRecord, onlineRecord)
          );
          if (matchingOffline) {
            this.clearConfirmedCreate(
              model,
              matchingOffline as unknown as IAppRecord,
              onlineRecord
            );
          }
        });
        for (const id of this.unconfirmedCreateIds.get(model) ?? []) {
          protectedRequestIds.add(id);
        }
        const protectedDocuments = offlineData.filter((document) => {
          const confirmedByServer = onlineData.some((onlineRecord) =>
            this.recordsShareIdentity(document, onlineRecord)
          );
          return (
            this.recordMatchesIds(document, protectedRequestIds, true) ||
            (document["dopa_sync_create_unconfirmed"] === true &&
              !confirmedByServer)
          );
        });
        const oldData = offlineData
          .filter((document) => !protectedDocuments.includes(document))
          .map((item) => ({
            _id: item._id,
            _rev: item._rev,
            _deleted: true,
          }));
        if (expectedCacheGeneration !== this.getCacheGeneration(model)) {
          return this.pouchService.search(model);
        }
        return this.pouchService.bulkDocs(oldData).pipe(
          switchMap(() => {
            if (
              expectedCacheGeneration !== this.getCacheGeneration(model)
            ) {
              return this.pouchService.search(model);
            }
            const newData = onlineData
              .filter(
                (item) =>
                  !protectedRequestIds.has(
                    this.preparePouchId(item).toString()
                  ) &&
                  (item.id === null ||
                    item.id === undefined ||
                    !protectedRequestIds.has(item.id.toString())) &&
                  !protectedDocuments.some(
                    (document) =>
                      (document["id"] !== undefined &&
                        document["id"] === item.id) ||
                      this.pouchService.unslugId(document._id).toString() ===
                        this.preparePouchId(item).toString()
                  )
              )
              .map((item) => ({
                ...item,
                _id: this.pouchService.slugId(
                  model,
                  this.preparePouchId(item)
                ),
              }));
            return this.pouchService.bulkDocs(newData).pipe(
              switchMap(() =>
                expectedCacheGeneration !== this.getCacheGeneration(model)
                  ? this.pouchService.search(model)
                  : of([
                      ...protectedDocuments.map((document) => {
                        const protectedDocument = {
                          ...document,
                          _id: this.pouchService.unslugId(document._id),
                        } as IAppRecord;
                        delete (protectedDocument as any)._rev;
                        return protectedDocument;
                      }),
                      ...newData.map((item) => ({
                        ...item,
                        _id: this.pouchService.unslugId(item._id),
                      })),
                    ])
              )
            );
          })
        );
      })
    );
  }

  private commitOnlineCreate(
    model: string,
    values: any
  ): Observable<RemoteCreateCommit> {
    const scopeGeneration = this.pouchService.getScopeGeneration();
    // Create the client identity once per method invocation. Retrying or
    // re-subscribing to the returned Observable must reuse the same idempotency
    // key.
    const createValues = {
      ...values,
      _id: values._id ?? this.generateAppId(),
    };
    const currentOdooValues = new AppRecord();

    return this.runRemoteMutation(model, () => {
      return this.prepareOdooValues(model, currentOdooValues, createValues).pipe(
        switchMap((preparedOdooValues) =>
          this.odooService.create(model, preparedOdooValues).pipe(
            map((createResponse) => {
              const committedRecord = {
                ...createValues,
                ...createResponse,
                _id: createValues._id,
              } as IAppRecord;
              const apiId = this.preparePouchId(committedRecord);
              const recordGeneration = this.bumpMutationGeneration(
                model,
                apiId
              );
              this.markCreateUnconfirmed(model, committedRecord);
              return {
                model,
                apiId,
                record: committedRecord,
                recordGeneration,
                scopeGeneration,
              };
            })
          )
        ),
        catchError((error) => this.errorService.handleError(error))
      );
    });
  }

  public createOnline(model: string, values: any): Observable<IAppRecord> {
    const scopeGeneration = this.pouchService.getScopeGeneration();
    return this.commitOnlineCreate(model, values).pipe(
      tap(() => this.pouchService.assertScopeGeneration(scopeGeneration)),
      switchMap((commit) =>
        this.runCacheMutation(model, () =>
          this.pouchService.upsert(model, {
            ...commit.record,
            dopa_sync_create_unconfirmed: true,
          })
        ).pipe(
          catchError((projectionError) => {
            console.error(
              "Odoo create committed but local projection failed",
              projectionError
            );
            return of(commit.record);
          }),
          tap(() => this.createReconciliation.next(commit)),
          map(() => commit.record)
        )
      )
    );
  }

  private reconcileCommittedCreate(
    commit: RemoteCreateCommit
  ): Observable<IAppRecord> {
    return this.pouchService.bindToScope(
      this.odooService.read(commit.model, commit.record.id).pipe(
      timeout(CREATE_RECONCILIATION_TIMEOUT),
      catchError((refreshError) => {
        console.error(
          "Odoo create committed but canonical refresh failed",
          refreshError
        );
        return EMPTY;
      }),
      tap((canonicalRecord) =>
        this.clearConfirmedCreate(
          commit.model,
          commit.record,
          canonicalRecord
        )
      ),
      switchMap((canonicalRecord) =>
        this.runCacheMutation(commit.model, () =>
          this.getRecordGeneration(commit.model, commit.apiId) ===
          commit.recordGeneration
            ? this.pouchService
                .upsert(commit.model, {
                  ...canonicalRecord,
                  _id: commit.apiId,
                  dopa_sync_create_unconfirmed: undefined,
                })
            : this.pouchService
                ._modify(
                  this.pouchService.slugId(commit.model, commit.apiId),
                  () => ({ dopa_sync_create_unconfirmed: undefined })
                )
                .pipe(
                  map((document) =>
                    this.exposeProjectedDocument(document, commit.apiId)
                  )
                )
        )
      )
      ),
      commit.scopeGeneration
    );
  }

  public createOffline(
    model: string,
    values: any,
    enqueueOptions: EnqueueOptions
  ): Observable<IAppRecord> {
    return this.runCacheMutation(model, () => {
      const createValues = { ...values };
      delete createValues.server_version;
      delete createValues._server_version;
      return this.preparePouchValues(createValues).pipe(
        tap((offlineValues) =>
          this.bumpMutationGeneration(model, offlineValues._id)
        ),
        switchMap((offlineValues) =>
          this.pouchService.create(model, offlineValues).pipe(
            switchMap((createdOffline) =>
              this.enqueueOnlineCreate(
                model,
                offlineValues,
                enqueueOptions
              ).pipe(
                tap((enqueuedGroup) => this.syncEnqueue.next(enqueuedGroup)),
                map(() => createdOffline)
              )
            )
          )
        )
      );
    });
  }

  private prepareOdooOne2ManyValues(
    model: string,
    currentValues: IOne2ManyItem[] | undefined,
    newValues: One2Many<IOne2ManyItem>
  ): Observable<any> {
    const currentMap = new Map(currentValues?.map((item) => [item._id, item]));

    const upsertValues$ = newValues.map((newItem) => {
      const currentItem = currentMap.get(newItem._id);
      if (currentItem) {
        newItem.id = currentItem.id;
      }
      currentMap.delete(newItem._id);

      return this.prepareOdooValues(model, null, newItem, false).pipe(
        map((normalizedItem) => {
          if (!currentItem || !isEqual({ ...currentItem }, { ...newItem })) {
            return normalizedItem;
          }
          return null;
        })
      );
    });

    const deleteValues$ = Array.from(currentMap.values()).map((item) =>
      of({ id: item.id })
    );

    return forkJoin([...deleteValues$, ...upsertValues$]).pipe(
      mergeMap((values) => values),
      filter((item) => item !== null),
      toArray()
    );
  }

  public prepareOdooValues(
    model: string,
    currentValues: Dictionary,
    newValues: Dictionary,
    isRoot = true
  ): Observable<IAppRecord> {
    const normalizedValues$: AppRecord$ = {};

    for (const field of Object.keys(newValues)) {
      if (field === "id") {
        if (!isRoot) {
          normalizedValues$[field] = of(newValues[field]);
        }
        continue;
      }

      if (
        field === "server_version" ||
        field === "_server_version" ||
        field === "dopa_sync_create_unconfirmed" ||
        field === "dopa_revision"
      ) {
        continue;
      }

      if (newValues[field] === null || newValues[field] === undefined) {
        normalizedValues$[field] = of(false);
        continue;
      }

      if (field !== "_id" && this.isValidAppId(newValues[field])) {
        normalizedValues$[field] = this.prepareOdooRelationId(newValues[field]);
        continue;
      }

      if (newValues[field] instanceof Many2One) {
        normalizedValues$[field] = of(newValues[field].id);
        continue;
      }

      if (newValues[field] instanceof One2Many) {
        const one2ManyValues$ = this.prepareOdooOne2ManyValues(
          model,
          currentValues[field],
          newValues[field]
        );
        normalizedValues$[field] = one2ManyValues$;
        continue;
      }

      if (newValues[field] instanceof Many2Many) {
        const many2ManyIds: IMany2ManyValues = [
          ...newValues[field].map(({ id }) => ({ id: id })),
        ];
        normalizedValues$[field] = of(many2ManyIds);
        continue;
      }

      normalizedValues$[field] = of(newValues[field]);
    }

    if (model === ATTACHMENT_MODEL) {
      normalizedValues$["datas"] = this.storageService.readFile(
        model,
        newValues["_id"]
      );
    }

    return forkJoin(normalizedValues$) as Observable<IAppRecord>;
  }

  private commitOnlineUpdate(
    model: string,
    apiId: ApiId,
    apiValues: IAppRecord
  ): Observable<RemoteUpdateCommit> {
    const scopeGeneration = this.pouchService.getScopeGeneration();
    return this.runRemoteMutation(model, () =>
      this.prepareOdooId(model, apiId).pipe(
      switchMap((odooId) =>
        forkJoin({
          localValues: this.pouchService
            .read(model, apiId)
            .pipe(catchError(() => of(apiValues))),
          currentOdooValues: this.odooService.read(model, odooId),
        }).pipe(
          switchMap(({ localValues, currentOdooValues }) =>
            this.prepareOdooValues(model, currentOdooValues, apiValues).pipe(
              switchMap((preparedOdooValues) => {
                const expectedVersion =
                  this.getServerVersion(apiValues) ??
                  this.getServerVersion(localValues) ??
                  this.getServerVersion(currentOdooValues);
                const remoteUpdate$ = expectedVersion
                  ? this.odooService
                      .conditionalUpdate(
                        model,
                        odooId,
                        preparedOdooValues,
                        expectedVersion
                      )
                      .pipe(map(({ version }) => version))
                  : this.odooService
                      .update(model, odooId, preparedOdooValues)
                      .pipe(map(() => undefined));

                return remoteUpdate$.pipe(
                  map((serverVersion) => {
                    const recordGeneration = this.bumpMutationGeneration(
                      model,
                      apiId
                    );
                    return {
                      model,
                      apiId,
                      odooId,
                      localValues,
                      submittedValues: apiValues,
                      expectedServerVersion: expectedVersion,
                      serverVersion,
                      recordGeneration,
                      scopeGeneration,
                    };
                  })
                );
              })
            )
          )
        )
      )
      )
    );
  }

  private projectCommittedUpdate(
    commit: RemoteUpdateCommit,
    preserveCurrentValues = false
  ): Observable<IAppRecord> {
    return this.runCacheMutation(commit.model, () =>
      this.projectCommittedUpdateToCache(commit, preserveCurrentValues)
    );
  }

  private projectCommittedUpdateToCache(
    commit: RemoteUpdateCommit,
    preserveCurrentValues = false
  ): Observable<IAppRecord> {
    const documentId = this.pouchService.slugId(commit.model, commit.apiId);
    const initialValues = {
      ...commit.localValues,
      ...(preserveCurrentValues ? {} : commit.submittedValues),
      _id: documentId,
      ...(typeof commit.odooId === "number" ? { id: commit.odooId } : {}),
      ...(commit.expectedServerVersion
        ? { server_version: commit.expectedServerVersion }
        : {}),
    } as IAppRecord;
    return this.pouchService
      ._modify(
        documentId,
        (currentDocument) => {
          const currentVersion = this.getServerVersion(currentDocument);
          if (
            (!preserveCurrentValues &&
              this.getRecordGeneration(commit.model, commit.apiId) !==
                commit.recordGeneration) ||
            (commit.expectedServerVersion &&
              currentVersion &&
              currentVersion !== commit.expectedServerVersion)
          ) {
            throw new UpdateProjectionSuperseded();
          }
          return {
            ...currentDocument,
            ...(preserveCurrentValues ? {} : commit.submittedValues),
            ...(currentDocument["id"] !== undefined
              ? { id: currentDocument["id"] }
              : typeof commit.odooId === "number"
                ? { id: commit.odooId }
                : {}),
            ...(commit.serverVersion
              ? { server_version: commit.serverVersion }
              : {}),
          };
        },
        initialValues
      )
      .pipe(
        map((document) =>
          this.exposeProjectedDocument(document, commit.apiId)
        ),
        catchError((error) => {
          if (!(error instanceof UpdateProjectionSuperseded)) {
            return throwError(() => error);
          }
          return this.pouchService
            .read(commit.model, commit.apiId)
            .pipe(map((record) => this.exposeRecord(record)));
        })
      );
  }

  private exposeProjectedDocument(
    document: IOfflineRecord,
    apiId: ApiId
  ): IAppRecord {
    const projectedDocument = {
      ...document,
      _id: this.pouchService.unslugId(document._id) ?? apiId.toString(),
    } as IAppRecord;
    delete (projectedDocument as any)._rev;
    delete projectedDocument._server_version;
    delete projectedDocument.dopa_sync_create_unconfirmed;
    return projectedDocument;
  }

  private exposeRecord(record: IAppRecord): IAppRecord {
    const exposedRecord = { ...record };
    delete exposedRecord.dopa_sync_create_unconfirmed;
    return exposedRecord;
  }

  private exposeRecords(records: IAppRecord[]): IAppRecord[] {
    return records.map((record) => this.exposeRecord(record));
  }

  private refreshCommittedUpdate(
    commit: RemoteUpdateCommit,
    projectedValues: IAppRecord
  ): Observable<IAppRecord> {
    return this.odooService.read(commit.model, commit.odooId).pipe(
      switchMap((canonicalValues) => {
        this.clearConfirmedCreate(commit.model, projectedValues, canonicalValues);
        const documentId = this.pouchService.slugId(
          commit.model,
          commit.apiId
        );
        return this.runCacheMutation(commit.model, () =>
          this.pouchService._modify(
            documentId,
            (currentDocument) => {
              const currentVersion = this.getServerVersion(currentDocument);
              const allowedVersions = [
                commit.expectedServerVersion,
                commit.serverVersion,
              ].filter(Boolean);
              if (
                this.getRecordGeneration(commit.model, commit.apiId) !==
                  commit.recordGeneration ||
                (currentVersion &&
                  allowedVersions.length > 0 &&
                  !allowedVersions.includes(currentVersion))
              ) {
                throw new UpdateProjectionSuperseded();
              }
              return {
                ...currentDocument,
                ...canonicalValues,
                dopa_sync_create_unconfirmed: undefined,
              };
            },
            {
              ...canonicalValues,
              _id: documentId,
            }
          ).pipe(
            map((document) =>
              this.exposeProjectedDocument(document, commit.apiId)
            ),
            catchError((error) => {
              if (!(error instanceof UpdateProjectionSuperseded)) {
                return throwError(() => error);
              }
              return this.pouchService
                ._modify(documentId, (currentDocument) => ({
                  ...currentDocument,
                  dopa_sync_create_unconfirmed: undefined,
                }))
                .pipe(
                  map((document) =>
                    this.exposeProjectedDocument(document, commit.apiId)
                  )
                );
            })
          )
        );
      }),
      timeout(UPDATE_RECONCILIATION_TIMEOUT),
      catchError((refreshError) => {
        console.error(
          "Odoo update committed but canonical refresh failed",
          refreshError
        );
        return of(projectedValues);
      })
    );
  }

  private updateOnline(
    model: string,
    apiId: ApiId,
    apiValues: IAppRecord
  ): Observable<IAppRecord> {
    const scopeGeneration = this.pouchService.getScopeGeneration();
    return this.commitOnlineUpdate(model, apiId, apiValues).pipe(
        tap(() => this.pouchService.assertScopeGeneration(scopeGeneration)),
        switchMap((commit) =>
          this.projectCommittedUpdate(commit).pipe(
            catchError((projectionError) => {
              console.error(
                "Odoo update committed but local projection failed",
                projectionError
              );
              return of({
                ...commit.localValues,
                ...commit.submittedValues,
                ...(commit.localValues.id !== undefined
                  ? { id: commit.localValues.id }
                  : typeof commit.odooId === "number"
                    ? { id: commit.odooId }
                    : {}),
                ...(commit.serverVersion
                  ? { server_version: commit.serverVersion }
                  : {}),
              } as IAppRecord);
            }),
            switchMap((projectedValues) =>
              this.refreshCommittedUpdate(commit, projectedValues)
            )
          )
        ),
        catchError((error) => this.errorService.handleError(error))
    );
  }

  public normalizePouchValues(
    currentValues: IAppRecord,
    newValues: IAppRecord
  ) {
    const normalizedValues = {
      ...currentValues,
      ...newValues,
    };
    const serverVersion =
      this.getServerVersion(newValues) ??
      this.getServerVersion(currentValues);
    delete normalizedValues._server_version;
    if (serverVersion !== undefined && serverVersion !== null) {
      normalizedValues.server_version = serverVersion;
    } else {
      delete normalizedValues.server_version;
    }
    return normalizedValues;
  }

  private updateOffline(
    model: string,
    id: ApiId,
    values: IAppRecord,
    enqueueOptions: EnqueueOptions
  ): Observable<IAppRecord> {
    return this.runCacheMutation(model, () => {
      this.bumpMutationGeneration(model, id);
      return this.pouchService.read(model, id).pipe(
        map((currentValues) =>
          this.normalizePouchValues(currentValues, values)
        ),
        switchMap((newValues) =>
          this.pouchService.update(model, id, newValues).pipe(
            switchMap((updatedOffline) =>
              this.enqueueOnlineUpdate(
                model,
                id,
                {
                  ...values,
                  server_version: updatedOffline.server_version,
                },
                enqueueOptions
              ).pipe(
                tap((enqueuedGroup) => this.syncEnqueue.next(enqueuedGroup)),
                map(() => updatedOffline)
              )
            )
          )
        )
      );
    });
  }

  private commitOnlineDelete(
    model: string,
    apiId: ApiId,
    remoteId?: number
  ): Observable<ApiId> {
    return this.runRemoteMutation(model, () => {
      this.bumpMutationGeneration(model, apiId);
      const onlineId$ =
        remoteId !== undefined
          ? of(remoteId)
          : this.prepareOdooId(model, apiId);
      return onlineId$.pipe(
        switchMap((onlineId) =>
          this.odooService.delete(model, onlineId).pipe(
            tap(() => this.clearUnconfirmedCreateIds(model, apiId, onlineId))
          )
        ),
        map(() => apiId),
        catchError((error) => this.errorService.handleError(error))
      );
    });
  }

  private projectCommittedDelete(model: string, apiId: ApiId): Observable<ApiId> {
    return this.runCacheMutation(model, () =>
      this.pouchService.delete(model, apiId).pipe(
        map(() => apiId),
        catchError((error) =>
          error?.status === 404 ? of(apiId) : throwError(() => error)
        )
      )
    );
  }

  private deleteOnline(model: string, apiId: ApiId): Observable<ApiId> {
    const scopeGeneration = this.pouchService.getScopeGeneration();
    return this.commitOnlineDelete(model, apiId).pipe(
      tap(() => this.pouchService.assertScopeGeneration(scopeGeneration)),
      switchMap(() =>
        this.projectCommittedDelete(model, apiId).pipe(
          catchError((projectionError) => {
            console.error(
              "Odoo delete committed but local projection failed",
              projectionError
            );
            return of(apiId);
          })
        )
      )
    );
  }

  private deleteOffline(
    model: string,
    apiId: ApiId,
    enqueueOptions: EnqueueOptions
  ): Observable<ApiId> {
    return this.runCacheMutation(model, () => {
      this.bumpMutationGeneration(model, apiId);
      return this.pouchService.read(model, apiId).pipe(
        switchMap((record) =>
          this.enqueueOnlineDelete(
            model,
            apiId,
            typeof record.id === "number" ? record.id : undefined,
            enqueueOptions
          ).pipe(map((enqueuedGroup) => ({ enqueuedGroup, record })))
        ),
        tap(({ enqueuedGroup, record }) => {
          this.clearConfirmedCreate(
            model,
            record,
            { _id: String(apiId) } as IAppRecord
          );
          this.syncEnqueue.next(enqueuedGroup);
        }),
        switchMap(() => this.pouchService.delete(model, apiId)),
        map(() => apiId)
      );
    });
  }

  public search(model: string): Observable<IAppRecord[]> {
    const scopeGeneration = this.pouchService.getScopeGeneration();
    if (this.isOnline()) {
      const cacheGeneration = this.getCacheGeneration(model);
      return this.pouchService.bindToScope(this.odooService.search(model).pipe(
        tap(() => this.pouchService.assertScopeGeneration(scopeGeneration)),
        switchMap((onlineResponse) =>
          cacheGeneration === this.getCacheGeneration(model)
            ? this.setOffline(model, onlineResponse, cacheGeneration)
            : this.pouchService.search(model)
        ),
        map((records) => this.exposeRecords(records)),
        catchError((error) => this.errorService.handleError(error))
      ), scopeGeneration);
    } else {
      return this.pouchService.bindToScope(this.pouchService
        .search(model)
        .pipe(
          map((records) => this.exposeRecords(records)),
          catchError((error) => this.errorService.handleError(error))
        ), scopeGeneration);
    }
  }

  private projectScopedSearch(
    projection: ScopedSearchProjection
  ): Observable<unknown[]> {
    return this.pouchService.bindToScope(this.runCacheMutation(projection.model, () => {
      if (
        projection.cacheGeneration !==
          this.getCacheGeneration(projection.model) ||
        projection.searchGeneration !==
          this.scopedSearchGenerations.get(projection.scopeKey)
      ) {
        return of([]);
      }
      return this.pouchService._search(OFFLINE_QUEUE_MODEL).pipe(
        switchMap((queueGroups) => {
          const queuedIds = this.getQueuedRequestIds(
            queueGroups,
            projection.model
          );
          const protectedIds = new Set([
            ...queuedIds.dirty,
            ...queuedIds.deleted,
          ]);
          return from(projection.records).pipe(
            concatMap((record) => {
              if (
                projection.cacheGeneration !==
                  this.getCacheGeneration(projection.model) ||
                projection.searchGeneration !==
                  this.scopedSearchGenerations.get(projection.scopeKey) ||
                this.recordMatchesIds(record, protectedIds)
              ) {
                return EMPTY;
              }
              return this.pouchService.upsert(projection.model, record);
            }),
            toArray()
          );
        })
      );
    }), projection.scopeGeneration);
  }

  /**
   * Resolve children for one parent without reconciling the complete model.
   * The local half is required because a queued CREATE is authoritative even
   * before it exists in Odoo. Queued local records win; otherwise the server
   * is authoritative for the requested scope.
   */
  public searchRelated(
    model: string,
    relationField: string,
    parent: IAppRecord
  ): Observable<IAppRecord[]> {
    const scopeGeneration = this.pouchService.getScopeGeneration();
    const parentIds = [parent?._id, parent?.id]
      .filter((id) => id !== null && id !== undefined)
      .map(String);
    const isRelated = (record: IAppRecord) => {
      const relation = record?.[relationField];
      const relationId =
        relation && typeof relation === "object"
          ? relation._id ?? relation.id
          : relation;
      return (
        relationId !== null &&
        relationId !== undefined &&
        relationId !== false &&
        parentIds.includes(String(relationId))
      );
    };
    const searchLocal = () =>
      this.pouchService.search(model).pipe(
        map((records) => this.exposeRecords(records.filter(isRelated)))
      );

    if (!this.isOnline() || typeof parent?.id !== "number") {
      return this.pouchService.bindToScope(searchLocal().pipe(
        catchError((error) => this.errorService.handleError(error))
      ), scopeGeneration);
    }

    const scopeKey = `${model}:${relationField}:server:${String(parent.id)}`;
    const cacheGeneration = this.getCacheGeneration(model);
    const searchGeneration = this.nextScopedSearchGeneration(scopeKey);

    return this.pouchService.bindToScope(this.odooService
      .search(model, {
        filters: [[relationField, "=", parent.id]],
      })
      .pipe(
      tap(() => this.pouchService.assertScopeGeneration(scopeGeneration)),
      switchMap((server) =>
        forkJoin({
          offlineData: this.pouchService._search(model),
          queueGroups: this.pouchService._search(OFFLINE_QUEUE_MODEL),
        }).pipe(
          map(({ offlineData, queueGroups }) => {
            const queuedIds = this.getQueuedRequestIds(
              queueGroups,
              model
            );
            const protectedIds = queuedIds.dirty;
            const local = offlineData
              .map((document) => {
                const record = {
                  ...document,
                  _id: this.pouchService.unslugId(document._id),
                } as IAppRecord;
                delete (record as any)._rev;
                return record;
              })
              .filter(isRelated);
            const matchedLocal = new Set<IAppRecord>();
            const projectionRecords: IAppRecord[] = [];
            const decision = server.flatMap<IAppRecord>((serverRecord) => {
              if (this.recordMatchesIds(serverRecord, queuedIds.deleted)) {
                return [];
              }
              const localRecord = local.find((record) =>
                this.recordsShareIdentity(record, serverRecord)
              );
              if (localRecord) {
                matchedLocal.add(localRecord);
                this.clearConfirmedCreate(model, localRecord, serverRecord);
              }
              const protectedRecord =
                this.recordMatchesIds(serverRecord, protectedIds) ||
                (localRecord &&
                  this.recordMatchesIds(localRecord, protectedIds));
              if (protectedRecord && localRecord) {
                return [
                  {
                    ...serverRecord,
                    ...localRecord,
                    id: serverRecord.id ?? localRecord.id,
                    _id: localRecord._id,
                  },
                ];
              }
              const canonicalRecord = {
                ...serverRecord,
                _id: localRecord?._id ?? serverRecord._id,
                dopa_sync_create_unconfirmed: undefined,
              } as IAppRecord;
              if (!protectedRecord) projectionRecords.push(canonicalRecord);
              return [canonicalRecord];
            });

            for (const localRecord of local) {
              if (matchedLocal.has(localRecord)) continue;
              if (this.recordMatchesIds(localRecord, queuedIds.deleted)) {
                continue;
              }
              if (
                this.recordMatchesIds(localRecord, protectedIds) ||
                this.recordMatchesIds(
                  localRecord,
                  this.unconfirmedCreateIds.get(model) ?? new Set<string>()
                ) || localRecord.dopa_sync_create_unconfirmed === true
              ) {
                decision.push(localRecord);
              }
            }

            this.scopedSearchProjection.next({
              model,
              scopeKey,
              records: projectionRecords,
              cacheGeneration,
              searchGeneration,
              scopeGeneration,
            });
            return decision.map((record) => {
              const exposedRecord = { ...record };
              delete exposedRecord.dopa_sync_create_unconfirmed;
              return exposedRecord;
            });
          })
        )
      ),
      catchError((error) => this.errorService.handleError(error))
    ), scopeGeneration);
  }

  public create(
    model: string,
    values: any,
    enqueueOptions: EnqueueOptions = null
  ): Observable<IAppRecord> {
    const created$ = this.isOnline()
      ? this.createOnline(model, values)
      : this.createOffline(model, values, enqueueOptions);
    return this.pouchService.bindToScope(
      created$.pipe(map((record) => this.exposeRecord(record)))
    );
  }

  public read(model: string, id: ApiId): Observable<IAppRecord> {
    const scopeGeneration = this.pouchService.getScopeGeneration();
    if (this.isOnline()) {
      return this.pouchService.bindToScope(this.prepareOdooId(model, id).pipe(
        switchMap((odooId) =>
          this.odooService
            .read(model, odooId)
            .pipe(
              tap(() =>
                this.pouchService.assertScopeGeneration(scopeGeneration)
              ),
              switchMap((odooResponse) =>
                this.pouchService.upsert(model, odooResponse)
              )
            )
        ),
        map((record) => this.exposeRecord(record)),
        catchError((error) => this.errorService.handleError(error))
      ), scopeGeneration);
    } else {
      return this.pouchService.bindToScope(this.pouchService
        .read(model, id)
        .pipe(
          map((record) => this.exposeRecord(record)),
          catchError((error) => this.errorService.handleError(error))
        ), scopeGeneration);
    }
  }

  public update(
    model: string,
    apiId: ApiId,
    values: any,
    enqueueOptions: EnqueueOptions = null
  ): Observable<IAppRecord> {
    const updated$ = this.isOnline()
      ? this.updateOnline(model, apiId, values)
      : this.updateOffline(model, apiId, values, enqueueOptions);
    return this.pouchService.bindToScope(
      updated$.pipe(map((record) => this.exposeRecord(record)))
    );
  }

  public delete(
    model: string,
    apiId: ApiId,
    enqueueOptions: EnqueueOptions = null
  ): Observable<ApiId> {
    if (this.isOnline()) {
      return this.pouchService.bindToScope(this.deleteOnline(model, apiId));
    } else {
      return this.pouchService.bindToScope(
        this.deleteOffline(model, apiId, enqueueOptions)
      );
    }
  }

  public fetchFile(apiId: any) {
    return this.http.get<{ datas: string; name: string; id: number }>(
      `${environment.apiUrl}/ir.attachment/${apiId}`,
      {
        params: {
          exclude_fields: JSON.stringify(["*"]),
          include_fields: JSON.stringify(["id", "name", "datas"]),
        },
      }
    );
  }

  public fetchReport(name: string, apiId: ApiId): Observable<string> {
    return this.http
      .get<string>(`${environment.apiUrl}/report/get_pdf`, {
        params: {
          report_name: name,
          ids: [apiId],
        },
      })
      .pipe(map((reportData) => reportData.replace(/(\r\n|\n|\r)/gm, "")));
  }

  public savePrototypes(savedObj: any): any {
    if (Array.isArray(savedObj)) {
      return {
        __prototype: savedObj.constructor.name.replace(/^_/, ""),
        items: savedObj.map((item) => this.savePrototypes(item)),
      };
    } else if (savedObj !== null && typeof savedObj === "object") {
      const plainObj = {
        ...savedObj,
        __prototype: savedObj.constructor.name.replace(/^_/, ""),
      };
      for (const key in plainObj) {
        if (plainObj.hasOwnProperty(key) && typeof plainObj[key] === "object") {
          plainObj[key] = this.savePrototypes(plainObj[key]);
        }
      }
      return plainObj;
    } else {
      return savedObj;
    }
  }

  public restorePrototypes(plainObj: any): any {
    if (plainObj !== null && typeof plainObj === "object") {
      let restoredObj: any;
      // Handle arrays
      if (plainObj.__prototype && plainObj.items) {
        let clonedObjs = Array.isArray(plainObj.items)
          ? Array.from(plainObj.items)
          : plainObj.items;

        switch (plainObj.__prototype) {
          case "One2Many":
            restoredObj = Object.setPrototypeOf(clonedObjs, One2Many.prototype);
            break;
          case "Many2Many":
            restoredObj = Object.setPrototypeOf(
              clonedObjs,
              Many2Many.prototype
            );
            break;
          default:
            restoredObj = clonedObjs;
        }
        restoredObj = restoredObj.map((item: any) =>
          this.restorePrototypes(item)
        );
      } else {
        // Handle object prototypes
        const clonedObj = Object.assign({}, plainObj);
        switch (plainObj.__prototype) {
          case "Many2One":
            restoredObj = Object.setPrototypeOf(clonedObj, Many2One.prototype);
            break;
          case "One2Many":
            restoredObj = Object.setPrototypeOf(clonedObj, One2Many.prototype);
            break;
          case "Many2Many":
            restoredObj = Object.setPrototypeOf(clonedObj, Many2Many.prototype);
            break;
          default:
            restoredObj = clonedObj;
        }
        delete restoredObj.__prototype;

        for (const key in restoredObj) {
          if (
            restoredObj.hasOwnProperty(key) &&
            restoredObj[key] !== null &&
            typeof restoredObj[key] === "object"
          ) {
            const clonedObj = Object.assign({}, restoredObj[key]);
            restoredObj[key] = this.restorePrototypes(clonedObj);
          }
        }
      }
      return restoredObj;
    } else {
      return plainObj; // Primitive types
    }
  }

  public enqueueOnlineRequest(
    method: "create" | "update" | "delete",
    model: string,
    id: ApiId,
    values: IAppRecord,
    options: EnqueueOptions = null,
    remoteId?: number
  ): Observable<EnqueuedGroup> {
    options = options ?? { model, id };

    const user = this.authService.getLoggedUser();
    const company_id = this.authService.getCompany();

    const groupId = this.pouchService.slugId(
      `${OFFLINE_QUEUE_MODEL}:${user.id}:${company_id}:${options.model}`,
      options.id
    );
    const groupValues = this.savePrototypes(values);
    const requestId = this.generateAppId();
    const initialGroup: EnqueuedGroup = {
      _id: groupId,
      user_id: user.id,
      company_id,
      queue: [],
    };

    return this.pouchService
      ._modify(
        groupId,
        (currentDocument) => {
          const currentGroup = currentDocument as unknown as EnqueuedGroup;
          const queue = currentGroup.queue ?? [];
          const maxSequence = Math.max(
            0,
            ...queue.map(({ sequence }) =>
              Number.isFinite(sequence) ? sequence : 0
            )
          );
          const request: EnqueuedRequest = {
            request_id: requestId,
            method,
            model,
            id,
            ...(remoteId !== undefined ? { remote_id: remoteId } : {}),
            values: groupValues,
            sequence: maxSequence + 1,
            expected_server_version: this.getServerVersion(values),
          };
          return {
            user_id: currentGroup.user_id ?? user.id,
            company_id: currentGroup.company_id ?? company_id,
            queue: [...queue, request],
          };
        },
        initialGroup
      )
      .pipe(
        map((pdbData) => {
          const group = { ...(pdbData as unknown as EnqueuedGroup) };
          delete (group as any)._rev;
          return group;
        }),
        catchError(() =>
          throwError(() => new Error("Failed to enqueue request"))
        )
      );
  }

  public enqueueOnlineCreate(
    model: string,
    values: any,
    options: EnqueueOptions = null
  ): Observable<EnqueuedGroup> {
    const id = values._id;
    return this.enqueueOnlineRequest("create", model, id, values, options);
  }

  public enqueueOnlineUpdate(
    model: string,
    id: ApiId,
    values: any,
    options: EnqueueOptions = null
  ): Observable<EnqueuedGroup> {
    return this.enqueueOnlineRequest("update", model, id, values, options);
  }

  public enqueueOnlineDelete(
    model: string,
    id: ApiId,
    remoteId?: number,
    options: EnqueueOptions = null
  ): Observable<EnqueuedGroup> {
    return this.enqueueOnlineRequest(
      "delete",
      model,
      id,
      null,
      options,
      remoteId
    );
  }

  public processEnqueuedRequest(
    request: EnqueuedRequest
  ): Observable<CommittedEnqueuedRequest> {
    let values: IAppRecord;
    try {
      values = this.restorePrototypes({ ...request.values });
    } catch (error) {
      return throwError(
        () => new Error("Failed to restore prototypes: " + error.message)
      );
    }

    switch (request.method) {
      case "create":
        return this.commitOnlineCreate(request.model, values).pipe(
          switchMap((commit) => {
            const serverVersion = this.getServerVersion(commit.record);
            return this.runCacheMutation(request.model, () =>
              this.pouchService._modify(
                this.pouchService.slugId(request.model, request.id),
                (currentDocument) => {
                  const projectedDocument = this.mergeCanonicalIdsByAppId(
                    currentDocument,
                    commit.record
                  );
                  return {
                    ...projectedDocument,
                    id: commit.record.id,
                    ...(serverVersion
                      ? { server_version: serverVersion }
                      : {}),
                    dopa_sync_create_unconfirmed: true,
                    _id: request.id.toString(),
                  };
                },
                {
                  ...values,
                  ...commit.record,
                  _id: request.id.toString(),
                }
              )
            ).pipe(
              map((projectedDocument) => {
                const projectedRecord = {
                  ...(projectedDocument as unknown as IAppRecord),
                  _id: request.id.toString(),
                };
                delete (projectedRecord as any)._rev;
                delete projectedRecord.dopa_sync_create_unconfirmed;
                return { commit, projectedRecord };
              })
            );
          }),
          tap(({ projectedRecord }) => {
            this.createSuccess.next({
              model: request.model,
              data: projectedRecord,
            });
          }),
          map(({ commit, projectedRecord }) => ({
            ...request,
            remote_id: commit.record.id,
            expected_server_version:
              this.getServerVersion(projectedRecord) ??
              this.getServerVersion(commit.record),
            remoteCreateCommit: commit,
          }))
        );

      case "update":
        return this.commitOnlineUpdate(request.model, request.id, {
          ...values,
          server_version:
            request.expected_server_version ??
            this.getServerVersion(values),
        }).pipe(
          map((commit) => ({
            ...request,
            expected_server_version:
              commit.serverVersion ?? request.expected_server_version,
            remoteUpdateCommit: commit,
          }))
        );

      case "delete":
        return this.commitOnlineDelete(
          request.model,
          request.id,
          request.remote_id
        ).pipe(
          map(() => ({
            ...request,
            remoteDeleteCommitted: true,
          }))
        );

      default:
        return throwError(
          () => new Error(`Unknown request method: ${request.method}`)
        );
    }
  }

  private mergeCanonicalIdsByAppId(localValue: any, serverValue: any): any {
    if (Array.isArray(localValue)) {
      if (!Array.isArray(serverValue)) return localValue;

      const serverItemsByAppId = new Map(
        serverValue
          .filter((item) => item?._id)
          .map((item) => [item._id, item])
      );
      return localValue.map((item) => {
        const serverItem = item?._id
          ? serverItemsByAppId.get(item._id)
          : undefined;
        return serverItem
          ? this.mergeCanonicalIdsByAppId(item, serverItem)
          : item;
      });
    }

    if (
      localValue &&
      serverValue &&
      typeof localValue === "object" &&
      typeof serverValue === "object"
    ) {
      const merged = { ...localValue };
      if (
        localValue._id &&
        serverValue._id &&
        localValue._id === serverValue._id &&
        serverValue.id !== undefined
      ) {
        merged.id = serverValue.id;
      }
      Object.keys(localValue).forEach((key) => {
        if (key !== "id" && serverValue[key] !== undefined) {
          merged[key] = this.mergeCanonicalIdsByAppId(
            localValue[key],
            serverValue[key]
          );
        }
      });
      return merged;
    }

    return localValue;
  }

  public processEnqueuedGroupSuccess(
    enqueuedGroup: EnqueuedGroup
  ): Observable<boolean> {
    return this.pouchService
      ._deleteIf(
        enqueuedGroup._id,
        (current) =>
          ((current as unknown as EnqueuedGroup).queue ?? []).length === 0
      )
      .pipe(
        tap((deleted) => deleted && this.syncSuccess.next(enqueuedGroup))
      );
  }

  public processEnqueuedGroupFailure(
    failedGroup: EnqueuedGroup,
    failedQueue: EnqueuedRequest[],
    error: unknown,
    completedRequestsOverride?: EnqueuedRequest[]
  ): Observable<never> {
    const persistedError = this.serializeEnqueuedError(error);
    const isConflict = persistedError["error"] === "concurrent_update";
    const completedRequests =
      completedRequestsOverride ??
      failedGroup.queue.filter(
        (request) =>
          !failedQueue.some((failedRequest) =>
            this.isSameEnqueuedRequest(request, failedRequest)
          )
      );
    return this.pouchService._modify(failedGroup._id, (currentDocument) => {
      const currentGroup = currentDocument as unknown as EnqueuedGroup;
      return {
        queue: this.removeEnqueuedRequests(
          currentGroup.queue ?? [],
          completedRequests
        ),
        errors: [...(currentGroup.errors ?? []), persistedError],
        status: isConflict ? "conflict" : "error",
      };
    }).pipe(
      tap((updatedGroup: unknown) =>
        this.syncFailure.next(updatedGroup as EnqueuedGroup)
      ),
      switchMap(() =>
        throwError(() => new EnqueuedGroupFailureHandled(error))
      )
    );
  }

  private serializeEnqueuedError(error: unknown): Record<string, unknown> {
    const httpPayload =
      typeof (error as any)?.error === "object" &&
      (error as any)?.error !== null
        ? (error as any).error
        : error;
    if (typeof httpPayload === "object" && httpPayload !== null) {
      const payload = httpPayload as Record<string, unknown>;
      return {
        error:
          typeof payload["error"] === "string"
            ? payload["error"]
            : "request_failed",
        error_descrip:
          typeof payload["error_descrip"] === "string"
            ? payload["error_descrip"]
            : typeof (error as any)?.message === "string"
              ? (error as any).message
              : "Offline synchronization failed",
        ...["model", "id", "expected_version", "current_version", "status"]
          .filter((key) => payload[key] !== undefined)
          .reduce((values, key) => ({ ...values, [key]: payload[key] }), {}),
      };
    }
    return {
      error: "request_failed",
      error_descrip:
        error instanceof Error ? error.message : String(error ?? "Unknown error"),
    };
  }

  public persistEnqueuedGroupProgress(
    group: EnqueuedGroup,
    completedRequest: EnqueuedRequest
  ): Observable<EnqueuedGroup> {
    return this.pouchService
      ._modify(group._id, (currentGroup: IOfflineRecord) => {
        const remainingQueue = this.removeEnqueuedRequests(
          (currentGroup as unknown as EnqueuedGroup).queue ?? [],
          [completedRequest]
        );
        return {
          ...currentGroup,
          queue: remainingQueue.map((request) =>
            request.model === completedRequest.model &&
            request.id === completedRequest.id
              ? {
                  ...request,
                  ...(completedRequest.expected_server_version &&
                  request.method === "update"
                    ? {
                        expected_server_version:
                          completedRequest.expected_server_version,
                      }
                    : {}),
                  ...(completedRequest.remote_id !== undefined &&
                  request.method === "delete"
                    ? { remote_id: completedRequest.remote_id }
                    : {}),
                }
              : request
          ),
        };
      })
      .pipe(
        map((updatedGroup: unknown) => {
          const persistedGroup = {
            ...(updatedGroup as EnqueuedGroup),
          };
          delete (persistedGroup as any)._rev;
          return persistedGroup;
        })
      );
  }

  private projectCompletedEnqueuedRequest(
    completedRequest: CommittedEnqueuedRequest,
    persistedGroup: EnqueuedGroup
  ): Observable<unknown> {
    if (
      completedRequest.method === "delete" &&
      completedRequest.remoteDeleteCommitted
    ) {
      return this.projectCommittedDelete(
        completedRequest.model,
        completedRequest.id
      ).pipe(
        catchError((projectionError) => {
          console.error(
            "Queued Odoo delete committed but local projection failed",
            projectionError
          );
          return of(completedRequest.id);
        }),
        tap(() =>
          this.deleteSuccess.next({
            model: completedRequest.model,
            id: completedRequest.id,
          })
        )
      );
    }

    if (
      completedRequest.method === "create" &&
      completedRequest.remoteCreateCommit
    ) {
      const hasFollowingUpdate = persistedGroup.queue.some(
        (request) =>
          request.method === "update" &&
          request.model === completedRequest.model &&
          request.id === completedRequest.id
      );
      if (!hasFollowingUpdate) {
        this.createReconciliation.next(completedRequest.remoteCreateCommit);
      }
      return of(null);
    }

    if (
      completedRequest.method !== "update" ||
      !completedRequest.remoteUpdateCommit
    ) {
      return of(null);
    }

    const commit = completedRequest.remoteUpdateCommit;
    const hasFollowingUpdate = persistedGroup.queue.some(
      (request) =>
        request.method === "update" &&
        request.model === completedRequest.model &&
        request.id === completedRequest.id
    );

    return this.projectCommittedUpdate(commit, hasFollowingUpdate).pipe(
        switchMap((projectedValues) =>
          hasFollowingUpdate
            ? of(projectedValues)
            : this.refreshCommittedUpdate(commit, projectedValues)
        ),
        tap((data) =>
          this.updateSuccess.next({ model: completedRequest.model, data })
        ),
        catchError((error) => {
          console.error(
            "Queued Odoo update committed but local projection failed",
            error
          );
          return of(null);
        })
      );
  }

  public processEnqueuedGroup(group: EnqueuedGroup): Observable<boolean> {
    this.syncStarted.next(group);
    return of([...group.queue].sort((a, b) => a.sequence - b.sequence)).pipe(
      expand((queue: EnqueuedRequest[]) => {
        if (queue.length === 0) {
          return EMPTY;
        }
        const request: EnqueuedRequest = queue[0];
        return this.processEnqueuedRequest(request).pipe(
          catchError((error) =>
            this.processEnqueuedGroupFailure(group, queue, error)
          ),
          switchMap((completedRequest) =>
            this.persistEnqueuedGroupProgress(group, completedRequest).pipe(
              switchMap((persistedGroup) =>
                this.projectCompletedEnqueuedRequest(
                  completedRequest,
                  persistedGroup
                ).pipe(
                  map(() =>
                    [...persistedGroup.queue].sort(
                      (a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)
                    )
                  )
                )
              ),
              catchError((error) =>
                this.processEnqueuedGroupFailure(
                  group,
                  queue.slice(1),
                  error,
                  [completedRequest]
                )
              )
            )
          )
        );
      }),
      last(),
      switchMap(() => this.processEnqueuedGroupSuccess(group)),
      switchMap((deleted) => {
        if (deleted) {
          return of(true);
        }
        return this.pouchService._read(group._id).pipe(
          switchMap((latestGroup) =>
            this.processEnqueuedGroup(latestGroup as unknown as EnqueuedGroup)
          )
        );
      }),
      catchError((error) => {
        if (error instanceof EnqueuedGroupFailureHandled) {
          return of(false);
        }
        return this.pouchService._read(group._id).pipe(
          catchError(() => of(group as unknown as IOfflineRecord)),
          tap((latestDocument) => {
            const latestGroup = latestDocument as unknown as EnqueuedGroup;
            this.syncFailure.next({
              ...latestGroup,
              errors: [...(latestGroup.errors ?? []), error],
            });
          }),
          map(() => false)
        );
      })
    );
  }

  private isSameEnqueuedRequest(
    left: EnqueuedRequest,
    right: EnqueuedRequest
  ): boolean {
    if (left.request_id && right.request_id) {
      return left.request_id === right.request_id;
    }
    return (
      left.sequence === right.sequence &&
      left.method === right.method &&
      left.model === right.model &&
      left.id === right.id
    );
  }

  private removeEnqueuedRequests(
    queue: EnqueuedRequest[],
    requestsToRemove: EnqueuedRequest[]
  ): EnqueuedRequest[] {
    const pendingRemovals = [...requestsToRemove];
    return queue.filter((queuedRequest) => {
      const removalIndex = pendingRemovals.findIndex((request) =>
        this.isSameEnqueuedRequest(queuedRequest, request)
      );
      if (removalIndex === -1) {
        return true;
      }
      pendingRemovals.splice(removalIndex, 1);
      return false;
    });
  }

  public searchOfflineQueue(): Observable<EnqueuedGroup[]> {
    const scopeGeneration = this.pouchService.getScopeGeneration();
    const user = this.authService.getLoggedUser();
    const company_id = this.authService.getCompany();
    return this.pouchService.bindToScope(this.pouchService._search(OFFLINE_QUEUE_MODEL).pipe(
      map((queue) =>
        queue
          .map((group) => {
            delete group._rev;
            return group as unknown as EnqueuedGroup;
          })
          .filter(
            (group) =>
              group.user_id === user.id && group.company_id === company_id
          )
      )
    ), scopeGeneration);
  }

  public deleteOfflineQueueGroup(id: string): Observable<boolean> {
    const scopeGeneration = this.pouchService.getScopeGeneration();
    const user = this.authService.getLoggedUser();
    const companyId = this.authService.getCompany();
    return this.pouchService.bindToScope(this.pouchService
      ._deleteIf(id, (document) => {
        const group = document as unknown as EnqueuedGroup;
        return group.user_id === user.id && group.company_id === companyId;
      })
      .pipe(
        switchMap((deleted) =>
          deleted
            ? of(true)
            : throwError(() => new Error("Offline queue group was not deleted"))
        )
      ), scopeGeneration);
  }

  public processOfflineQueue(): Observable<boolean> {
    return this.pouchService.bindToScope(this.searchOfflineQueue().pipe(
      switchMap((offlineQueue) => from(offlineQueue)),
      filter((enqueuedGroup) => enqueuedGroup.status !== "conflict"),
      concatMap((enqueuedGroup) => this.processEnqueuedGroup(enqueuedGroup))
    ));
  }
}
